import * as bootstrap from 'bootstrap';
import { get, post, notifier } from '../util';
import * as Highcharts from 'highcharts';
import { updateChartTotals, setActiveStepper } from './shared';

// Constants (verbatim from modal.ts)
const GYM_COLUMNS = ['chest', 'biceps', 'back', 'triceps', 'legs', 'shoulders', 'core', 'forearms', 'rock', 'cardio'] as const;
const GYM_LABELS  = ['Chest', 'Biceps', 'Back', 'Triceps', 'Legs', 'Shoulders', 'Core', 'Forearms', 'Rock Climbing', 'Cardio'] as const;
const GYM_LOCATIONS = [
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'planet',    label: 'Planet Fitness' },
    { value: 'altrock',   label: 'Altrock' },
] as const;

interface GymSessionData {
    gym_id: number;
    gym: string;
    chest: boolean; biceps: boolean; back: boolean; triceps: boolean;
    legs: boolean; shoulders: boolean; core: boolean; forearms: boolean;
    rock: boolean; cardio: boolean;
}

// Module state
let currentDate = '';
let newCardCounter = 0;
let hiddenListenerAttached = false;
let modalListenersBound = false;

export function initGymModal(): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    if (!hiddenListenerAttached) {
        modalEl.addEventListener('hidden.bs.modal', () => {
            // Defensively restore footer (idempotent — also handled by initRunsModal)
            const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
            if (footer) footer.classList.remove('d-none');
            currentDate = '';
        });
        hiddenListenerAttached = true;
    }

    if (!modalListenersBound) {
        modalListenersBound = true;

        // D-03: autofocus the first checkbox once the modal is actually shown. Self-selects
        // by querying gym's own card markup — a no-op when a different one of the four modal
        // types is the one currently rendered (Pattern 1).
        modalEl.addEventListener('shown.bs.modal', () => {
            const firstCheckbox = modalEl.querySelector(
                '#chart-edit-modal-body .gym-card input[type="checkbox"]'
            ) as HTMLInputElement | null;
            firstCheckbox?.focus();
        });

        // D-04/D-05: container-level Enter-to-save. Falls through for <button> targets
        // (Add Session / per-card delete) so their native action is preserved. Self-selects
        // via the gym-specific Save All button (`.js-gym-save-all` is unique to this modal —
        // no cross-modal class collision) so this listener is a no-op when runs/stairmaster/
        // the shared alc/weight/snap modal is the one currently rendered.
        modalEl.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key !== 'Enter') return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'BUTTON') return;
            const saveAllBtn = modalEl.querySelector('.js-gym-save-all') as HTMLButtonElement | null;
            if (!saveAllBtn) return;
            e.preventDefault();
            handleGymSaveAll(saveAllBtn);
        });
    }
}

/**
 * Builds the activity-cards container + per-card skeleton/data + Add Session/Save All controls
 * into the already-cleared body (Phase 262, HOME-01/02). `sessions === null` renders the D-01
 * show-first blank skeleton card; an empty array renders the same blank-card empty state (a
 * step that resolved to zero sessions); a populated array renders one real card per session.
 * Returns the new container element.
 */
function buildGymBody(sessions: GymSessionData[] | null): HTMLElement {
    const body = document.getElementById('chart-edit-modal-body')!;

    const container = document.createElement('div');
    container.className = 'activity-cards-container';
    body.appendChild(container);

    if (sessions === null || sessions.length === 0) {
        // Blank card \u2014 same shape as the existing empty state, so there is nothing to change
        // if getGymFromDate returns zero sessions. D-03's autofocus target (the first
        // checkbox) is real and focusable from the very first paint.
        renderGymCard(container, null);
    } else {
        sessions.forEach(session => renderGymCard(container, session));
    }

    // + Add Session button
    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn btn-outline-success w-100 mt-2';
    addBtn.textContent = '+ Add Session';
    addBtn.addEventListener('click', () => {
        const card = renderGymCard(container, null);
        card.scrollIntoView({ behavior: 'smooth' });
    });
    body.appendChild(addBtn);

    // Save All button
    const saveAllBtn = document.createElement('button');
    saveAllBtn.type = 'button';
    saveAllBtn.className = 'btn btn-primary w-100 mt-2 js-gym-save-all';
    saveAllBtn.innerHTML = '<span class="js-gym-save-label">Save All</span><span class="spinner-border spinner-border-sm d-none js-gym-spinner" role="status"></span>';
    saveAllBtn.addEventListener('click', () => handleGymSaveAll(saveAllBtn));
    body.appendChild(saveAllBtn);

    return container;
}

/**
 * Fetch-and-populate half of openGymModal() (Phase 262, HOME-01/02) \u2014 extracted so the date-nav
 * step path can reload the body for a new date without closing/reopening the modal.
 *
 * Open path (blankUntilLoaded=false): behaves exactly as before \u2014 the body is cleared and the
 * D-01 show-first skeleton card + Add Session/Save All controls are built synchronously, then
 * the first card is populated in place once getGymFromDate resolves (never rebuilt, so a
 * shown.bs.modal focus already landed on it survives).
 *
 * Step path (blankUntilLoaded=true): the body is cleared and left EMPTY (P-04 \u2014 no skeleton
 * card, no spinner) until the fetch resolves, then the whole container/cards/controls are built
 * fresh from the response via buildGymBody().
 */
function renderBodyForDate(date: string, blankUntilLoaded: boolean, done?: (ok: boolean) => void): void {
    const body = document.getElementById('chart-edit-modal-body');
    if (!body) { done?.(false); return; }
    body.innerHTML = '';

    let openContainer: HTMLElement | null = null;
    if (!blankUntilLoaded) {
        openContainer = buildGymBody(null);
    }

    get('?do=getGymFromDate&date=' + date, {}, (data: any) => {
        if (blankUntilLoaded && data.status !== 1) { done?.(false); return; }

        // CR-01 stale-response guard (mirrors mood-modal.ts:207). Two getGymFromDate requests can
        // be in flight for different dates -- openModal()'s own fetch is not covered by date-nav's
        // _busy lock. Dropping the stale one prevents both the wrong-date save (CR-01) and the
        // duplicated card list this builder would otherwise appendChild a second time (WR-01).
        if (date !== currentDate) { done?.(true); return; }

        const sessions: GymSessionData[] = data.status === 1 ? (data.sessions || []) : [];

        if (!blankUntilLoaded) {
            if (sessions.length === 0) {
                // Skeleton blank card already matches the empty state \u2014 nothing to populate.
                done?.(true);
                return;
            }
            // Populate the already-rendered skeleton card in place with the first session
            // (never rebuilds body.innerHTML here, so a shown.bs.modal focus that already
            // landed on it is never destroyed by this late response), then append the rest.
            const firstCard = openContainer!.querySelector('.gym-card') as HTMLElement | null;
            if (firstCard) {
                populateGymCard(firstCard, sessions[0]);
            }
            for (let i = 1; i < sessions.length; i++) {
                renderGymCard(openContainer!, sessions[i]);
            }
            done?.(true);
            return;
        }

        buildGymBody(sessions);
        done?.(true);
    });
}

/**
 * Date-nav step callback (Phase 262, HOME-01/02) \u2014 the ModalDateStepper handed to shared.ts's
 * active-stepper registry from inside openGymModal().
 */
function step(newDate: string, done: (ok: boolean) => void): void {
    // T-262-30: currentDate/newCardCounter are reassigned BEFORE the fetch is issued.
    currentDate = newDate;
    newCardCounter = 0;
    renderBodyForDate(newDate, true, done);
}

export function openGymModal(date: string): void {
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    currentDate = date;
    newCardCounter = 0;

    // Phase 262 HOME-01/02: register this modal's step implementation before the modal is
    // shown, next to the existing currentDate assignment.
    setActiveStepper(date, step);

    // D-01 show-first: title/footer are computable synchronously (no field in them depends on
    // the AJAX response), so render + show BEFORE the getGymFromDate call. P-01: bare, date-
    // free title \u2014 the shared date-nav header owns the date.
    const title = document.getElementById('chart-edit-modal-title');
    if (title) title.textContent = 'Gym';

    const footer = modalEl.querySelector('.modal-footer') as HTMLElement | null;
    if (footer) footer.classList.add('d-none');

    renderBodyForDate(date, false);

    const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl, { backdrop: true, keyboard: true });
    modalInstance.show();
}

function renderGymCard(container: HTMLElement, session: GymSessionData | null): HTMLElement {
    const cardId = session ? 'gym-card-' + session.gym_id : 'gym-card-new-' + (++newCardCounter);

    const card = document.createElement('div');
    card.className = 'card mb-3 gym-card';
    card.id = cardId;
    card.dataset.gymId = session ? String(session.gym_id) : '';
    card.dataset.type = 'gym';

    // Header with location select + delete button
    const header = document.createElement('div');
    header.className = 'card-header py-1 px-2 d-flex justify-content-between align-items-center';

    const select = document.createElement('select');
    select.className = 'form-select form-select-sm js-gym-location';

    const blankOption = document.createElement('option');
    blankOption.value = '';
    blankOption.textContent = 'Select gym...';
    select.appendChild(blankOption);

    for (const loc of GYM_LOCATIONS) {
        const opt = document.createElement('option');
        opt.value = loc.value;
        opt.textContent = loc.label;
        if (session && session.gym === loc.value) {
            opt.selected = true;
        }
        select.appendChild(opt);
    }

    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'btn btn-sm btn-link text-danger p-0 js-card-delete';
    deleteBtn.textContent = '\u{1F5D1}';
    deleteBtn.title = 'Delete';

    header.appendChild(select);
    header.appendChild(deleteBtn);
    card.appendChild(header);

    // Body with 2-column checkbox grid
    const cardBody = document.createElement('div');
    cardBody.className = 'card-body py-2 px-2';

    const grid = document.createElement('div');
    grid.style.display = 'grid';
    grid.style.gridTemplateColumns = 'repeat(2, 1fr)';
    grid.style.gap = '4px 12px';

    GYM_COLUMNS.forEach((col, i) => {
        const checkId = cardId + '-' + col;
        const wrapper = document.createElement('div');
        wrapper.className = 'form-check';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'form-check-input js-gym-' + col;
        checkbox.id = checkId;
        if (session && !!session[col]) {
            checkbox.checked = true;
        }

        const label = document.createElement('label');
        label.className = 'form-check-label';
        label.htmlFor = checkId;
        label.textContent = GYM_LABELS[i];

        wrapper.appendChild(checkbox);
        wrapper.appendChild(label);
        grid.appendChild(wrapper);
    });

    cardBody.appendChild(grid);
    card.appendChild(cardBody);

    deleteBtn.addEventListener('click', () => handleDeleteGymSession(card, session));

    container.appendChild(card);
    return card;
}

/**
 * Populate a skeleton card (originally rendered with session=null) in place once the
 * getGymFromDate response resolves — sets the real gym_id/location/checkbox state on the
 * SAME DOM node (never rebuilds it) so a shown.bs.modal focus already landed on one of its
 * checkboxes is never destroyed by this late response (D-01).
 */
function populateGymCard(card: HTMLElement, session: GymSessionData): void {
    card.id = 'gym-card-' + session.gym_id;
    card.dataset.gymId = String(session.gym_id);

    const select = card.querySelector('.js-gym-location') as HTMLSelectElement | null;
    if (select) select.value = session.gym;

    GYM_COLUMNS.forEach(col => {
        const checkbox = card.querySelector('.js-gym-' + col) as HTMLInputElement | null;
        if (checkbox) checkbox.checked = !!session[col];
    });

    // Re-bind delete to the now-known session — the skeleton's delete handler closed over
    // session=null (an unsaved-card removal, not a real DELETE call).
    const deleteBtn = card.querySelector('.js-card-delete') as HTMLButtonElement | null;
    if (deleteBtn) {
        const freshDeleteBtn = deleteBtn.cloneNode(true) as HTMLButtonElement;
        deleteBtn.replaceWith(freshDeleteBtn);
        freshDeleteBtn.addEventListener('click', () => handleDeleteGymSession(card, session));
    }
}

function handleDeleteGymSession(card: HTMLElement, session: GymSessionData | null): void {
    if (!window.confirm('Delete this gym session?')) return;

    // Unsaved / new card — remove immediately
    if (!session || !session.gym_id) {
        card.remove();
        return;
    }

    // Optimistic removal
    const container = card.parentElement as HTMLElement;
    const nextSibling = card.nextSibling;
    card.remove();

    post('?do=deleteGymSession&gymId=' + session.gym_id, {}, (data: any) => {
        if (data.status === 1) {
            // No per-day chart-point update on delete (per user decision) — but the
            // range-scoped titlebar total MUST still refresh (D-10/WARNING-1). `data.totals`
            // is the FLAT gym totals list (single-titlebar) — pass directly, no wrapper.
            updateChartTotals('gym', data.totals);
            notifier.success('Session deleted');
        } else {
            // Rollback
            container.insertBefore(card, nextSibling);
            alert(data.message || 'Failed to delete session.');
        }
    });
}

function handleGymSaveAll(saveAllBtn: HTMLButtonElement): void {
    if (saveAllBtn.disabled) return;   // D-04 re-entrancy guard — Enter-to-save calls this
                                        // directly, bypassing native disabled-button click suppression
    const modalEl = document.getElementById('chart-edit-modal');
    if (!modalEl) return;

    const saveLabel = saveAllBtn.querySelector('.js-gym-save-label') as HTMLElement;
    const spinner = saveAllBtn.querySelector('.js-gym-spinner') as HTMLElement;

    saveAllBtn.disabled = true;
    saveLabel.classList.add('d-none');
    spinner.classList.remove('d-none');

    // Remove previous inline error
    const prevError = modalEl.querySelector('.gym-inline-error');
    if (prevError) prevError.remove();

    const cards = Array.from(document.querySelectorAll('#chart-edit-modal-body .gym-card')) as HTMLElement[];

    // Validation: every card must have a gym location selected
    for (const card of cards) {
        const gymSelect = card.querySelector('.js-gym-location') as HTMLSelectElement;
        if (!gymSelect.value) {
            saveAllBtn.disabled = false;
            saveLabel.classList.remove('d-none');
            spinner.classList.add('d-none');
            const body = document.getElementById('chart-edit-modal-body');
            if (body) {
                const errorEl = document.createElement('div');
                errorEl.className = 'text-danger small mt-2 gym-inline-error';
                errorEl.textContent = 'Please select a gym for all sessions';
                body.appendChild(errorEl);
            }
            return;
        }
    }

    // No cards — just close modal
    if (cards.length === 0) {
        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        return;
    }

    const promises: Promise<any>[] = cards.map(card => {
        const gymIdAttr = card.dataset.gymId || '';
        const gymId = gymIdAttr !== '' ? parseInt(gymIdAttr) : null;
        const gym = (card.querySelector('.js-gym-location') as HTMLSelectElement).value;

        const payload: Record<string, string | number> = { gym };
        GYM_COLUMNS.forEach(col => {
            const checkbox = card.querySelector('.js-gym-' + col) as HTMLInputElement;
            payload[col] = checkbox.checked ? 1 : 0;
        });
        if (gymId !== null && gymId > 0) payload.gym_id = gymId;

        return new Promise<any>((resolve, reject) => {
            post('?do=upsertGymSession&date=' + currentDate, payload, (data: any) => {
                if (data.status === 1) {
                    // WR-02 (220-REVIEW) id write-back: stamp the returned id onto THIS
                    // card's dataset as soon as its own write lands, so a retry after a
                    // partial Save-All failure (e.g. via the Enter-triggered retry path)
                    // UPDATEs the now-known row instead of re-INSERTing a duplicate.
                    if (data.gym_id !== undefined && data.gym_id !== null) {
                        card.dataset.gymId = String(data.gym_id);
                    }
                    resolve(data);
                } else {
                    reject(data.message || 'Save failed');
                }
            });
        });
    });

    Promise.all(promises).then(results => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        // Update chart using gym_totals from last result (per-day point — unchanged; a
        // per-DAY aggregate is order-independent across cards written for the same date).
        const lastResult = results[results.length - 1];
        const gymTotals = lastResult.gym_totals;
        if (gymTotals) {
            updateGymChartPoints(currentDate, gymTotals);
        }
        // WR-02 (220-REVIEW) totals race fix: the last array-indexed result's `.totals` is NOT
        // reliably the most-recent range-scoped snapshot — Promise.all's concurrent POSTs
        // can resolve out of server-completion order, so the "last" array entry may have
        // been computed before a sibling card's write actually landed. Issue ONE dedicated
        // re-fetch via the existing fetchGraphData signal (already used by customize.ts's
        // on-demand graph reveal) now that every write has settled, instead of trusting any
        // single per-card response's totals snapshot.
        post('?do=fetchGraphData&key=gym', {}, (graphResp: any) => {
            if (graphResp.success && graphResp.graph) {
                updateChartTotals('gym', graphResp.graph.totals);
            } else {
                // Fall back to the pre-existing (racy but non-fatal) behavior rather than
                // leaving the titlebar stale outright.
                updateChartTotals('gym', lastResult.totals);
            }
        });

        const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
        modalInstance.hide();
        notifier.success('Saved');
    }).catch(errMsg => {
        saveAllBtn.disabled = false;
        saveLabel.classList.remove('d-none');
        spinner.classList.add('d-none');

        const body = document.getElementById('chart-edit-modal-body');
        if (body) {
            const errorEl = document.createElement('div');
            errorEl.className = 'text-danger small mt-2 gym-inline-error';
            errorEl.textContent = typeof errMsg === 'string' ? errMsg : 'An error occurred. Please try again.';
            body.appendChild(errorEl);
        }
    });
}

function updateGymChartPoints(date: string, totals: Record<string, number>): void {
    //@ts-ignore
    const chart = (Highcharts.charts as (Highcharts.Chart | undefined)[]).find((c: any) => c?.renderTo?.id === 'chart_gym');
    if (!chart) return;

    GYM_COLUMNS.forEach((col, i) => {
        const series = chart.series[i];
        if (!series) return;
        //@ts-ignore
        const pointStart = series.options.pointStart as number;
        const idx = Math.round((Date.parse(date) - pointStart) / 86400000);
        //@ts-ignore
        const point = series.data[idx];
        if (point !== undefined) {
            //@ts-ignore
            point.update(totals[col] ? 1 : null);
        }
    });
}
